import axios from 'axios';
import { config } from '../config/environment';

// Create axios instance for quiz API
const quizApiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
quizApiClient.interceptors.request.use((axiosConfig) => {
  const token = localStorage.getItem('quiz_token');
  if (token) {
    axiosConfig.headers.Authorization = `Bearer ${token}`;
  }

  // Debug logging
  if (config.debug) {
    console.log(`[Quiz API] ${axiosConfig.method?.toUpperCase()} ${axiosConfig.url}`, {
      data: axiosConfig.data,
      params: axiosConfig.params,
    });
  }

  return axiosConfig;
});

// Response interceptor for error handling
quizApiClient.interceptors.response.use(
  (response) => {
    if (config.debug) {
      console.log(`[Quiz API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`, {
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    if (config.debug) {
      console.error(`[Quiz API] ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status}`, {
        error: error.response?.data,
        message: error.message,
      });
    }

    if (error.response?.status === 401) {
      // Token expired or invalid
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        localStorage.removeItem('quiz_token');
        localStorage.removeItem('quiz_refresh_token');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Types
export interface CreateQuizSessionRequest {
  subjectId: string;
  questionCount: number;
  difficulties?: number[];
  preloadQuestions?: boolean;
  preloadCount?: number;
}

export interface QuizSessionResponse {
  id: string;
  subjectId: string;
  subjectName: string;
  totalQuestions: number;
  completedQuestions: number;
  status: 'active' | 'completed' | 'paused' | 'cancelled';
  currentScore: number;
  accuracy: number;
  questions: Question[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface Question {
  id: string;
  text: string;
  difficulty: number;
  explanation?: string;
  answerOptions: AnswerOption[];
}

export interface AnswerOption {
  id: string;
  text: string;
  isCorrect: boolean;
  order: number;
}

export interface QuizAnswer {
  questionId: string;
  selectedAnswerId: string;
  timeSpent: number;
}

export interface SubmitAnswerRequest {
  answer: {
    questionId: string;
    answerId: string;
    timeSpent: number;
  };
}

export interface SubmitSessionRequest {
  answers: {
    questionId: string;
    answerId: string;
    timeSpent: number;
  }[];
}

export interface QuizStats {
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  accuracy: number;
  totalTimeSpent: number;
  averageTimePerQuestion: number;
  difficultyBreakdown: {
    level: number;
    total: number;
    correct: number;
  }[];
}

export interface WrongAnswer {
  questionId: string;
  question: Question;
  selectedAnswer: AnswerOption;
  correctAnswer: AnswerOption;
  timeSpent: number;
}

// Quiz API service
export const quizApi = {
  // Crea nuova sessione quiz
  async createSession(subjectId: string, questionCount: number, options?: {
    difficulties?: number[];
    preloadQuestions?: boolean;
    preloadCount?: number;
  }): Promise<QuizSessionResponse> {
    const request: CreateQuizSessionRequest = {
      subjectId,
      questionCount,
      difficulties: options?.difficulties,
      preloadQuestions: options?.preloadQuestions ?? true,
      preloadCount: options?.preloadCount ?? 50
    };

    const response = await quizApiClient.post('/quiz-sessions', request);
    return response.data;
  },

  // Ottieni sessione esistente
  async getSession(sessionId: string): Promise<QuizSessionResponse> {
    const response = await quizApiClient.get(`/quiz-sessions/${sessionId}`);
    return response.data;
  },

  // Ottieni domande casuali per quiz locale
  async getRandomQuestions(subjectId: string, limit: number = 10): Promise<Question[]> {
    const response = await quizApiClient.get(`/questions/random/${subjectId}?limit=${limit}`);
    return response.data;
  },

  // Ottieni numero totale di domande per una materia
  async getQuestionCount(subjectId: string): Promise<{ subjectId: string; totalQuestions: number; activeQuestions: number }> {
    console.log('📊 [DEBUG] quizApi.getQuestionCount chiamato con subjectId:', subjectId);
    try {
      const response = await quizApiClient.get(`/questions/count/${subjectId}`);
      console.log('📊 [DEBUG] quizApi.getQuestionCount response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ERROR] quizApi.getQuestionCount errore:', error);
      throw error;
    }
  },

  // Invia risposta singola
  async submitAnswer(sessionId: string, questionId: string, answerId: string, timeSpent: number): Promise<QuizSessionResponse> {
    const request = {
      answer: {
        questionId,
        answerId,
        timeSpent
      }
    };

    const response = await quizApiClient.post(`/quiz-sessions/${sessionId}/answers`, request);
    return response.data;
  },

  // Submit completo sessione
  async submitSession(sessionId: string, answers: QuizAnswer[]): Promise<QuizSessionResponse> {
    const request: SubmitSessionRequest = {
      answers: answers.map(answer => ({
        questionId: answer.questionId,
        answerId: answer.selectedAnswerId,
        timeSpent: answer.timeSpent
      }))
    };

    const response = await quizApiClient.post(`/quiz-sessions/${sessionId}/submit`, request);
    return response.data;
  },

  // Submit risultati finali di quiz locale
  async submitQuizResults(sessionId: string, answers: QuizAnswer[], totalTimeSpent: number): Promise<QuizSessionResponse> {
    const request = {
      answers: answers.map(answer => ({
        questionId: answer.questionId,
        answerId: answer.selectedAnswerId,
        timeSpent: answer.timeSpent
      })),
      finishedAt: new Date().toISOString()
    };

    console.log('📤 [DEBUG] Invio submitQuizResults:', {
      sessionId,
      totalAnswers: answers.length,
      totalTimeSpent,
      request: request
    });

    const response = await quizApiClient.post(`/quiz-sessions/${sessionId}/submit`, request);
    return response.data;
  },

  // Statistiche sessione
  async getSessionStats(sessionId: string): Promise<QuizStats> {
    const response = await quizApiClient.get(`/quiz-sessions/${sessionId}/stats`);
    return response.data;
  },

  // Review errori
  async getWrongAnswers(sessionId: string): Promise<WrongAnswer[]> {
    const response = await quizApiClient.get(`/quiz-sessions/${sessionId}/wrong-answers`);
    return response.data;
  },

  // Dettagli domanda singola
  async getQuestion(questionId: string): Promise<Question> {
    const response = await quizApiClient.get(`/questions/${questionId}`);
    return response.data;
  },

  // Pre-carica domande per materia
  async preloadQuestions(subjectId: string, count: number = 50): Promise<Question[]> {
    return this.getRandomQuestions(subjectId, count);
  },

  // Cache status per materia
  async getCacheStatus(subjectId: string): Promise<{ isCached: boolean; lastUpdated?: string; questionCount: number }> {
    try {
      const response = await quizApiClient.get(`/quiz-sessions/cache/status/${subjectId}`);
      return response.data;
    } catch (error) {
      // Se l'endpoint non esiste, ritorna stato di default
      return { isCached: false, questionCount: 0 };
    }
  },

  // Pulisce cache
  async clearCache(): Promise<void> {
    try {
      await quizApiClient.post('/quiz-sessions/cache/clear');
    } catch (error) {
      // Se l'endpoint non esiste, ignora l'errore
      console.warn('Cache clear endpoint not available');
    }
  },

  // Duplica una sessione con le stesse domande
  async duplicateSession(originalSessionId: string): Promise<QuizSessionResponse> {
    const response = await quizApiClient.post(`/quiz-sessions/duplicate/${originalSessionId}`);
    return response.data;
  },

  // Cancella una sessione quiz
  async deleteSession(sessionId: string): Promise<void> {
    await quizApiClient.delete(`/quiz-sessions/${sessionId}`);
  }
};
